import { useMemo } from 'react';
import { EntitySort, type ReferenceTitleDto } from '@gr/core/content/entities/dto';
import { ReferenceTargetKind } from '@gr/core/content/entities/fields';
import { useConfig, useServices } from '../../../app/di';
import { pageOf, titlesOf, type ReferenceLookup } from './model/reference-search';

/**
 * ENT-UC-8: поиск целей для полей-ссылок формы — записи типа или посты, страницами и по строке
 * поиска, тем же списком, что у страниц записей и постов. Заголовки уже выбранных целей — из
 * referenceTitles записи или поста (ENT-UC-8.CC-2).
 */
export function useReferenceSearch(blogId: string, referenceTitles?: Readonly<Record<string, ReferenceTitleDto>> | null): ReferenceLookup {
  const { api } = useServices();
  const titles = useMemo(() => titlesOf(referenceTitles), [referenceTitles]);
  // Сколько целей автокомплит показывает за раз (ui.references.pageSize).
  const size = useConfig().ui.references.pageSize;
  return useMemo(
    () => ({
      titles,
      search: async (target, query, offset, signal) => {
        const page = { q: query.trim(), limit: size + 1, offset };
        if (target?.kind === ReferenceTargetKind.Entity) {
          const list = await api.entities.list(blogId, target.entityTypeId ?? '', { ...page, sort: EntitySort.Title }, signal);
          return list.success && list.data ? pageOf(list.data.items.map((e) => ({ id: e.id, title: e.title })), size) : null;
        }
        const posts = await api.posts.list(blogId, page, signal);
        return posts.success && posts.data ? pageOf(posts.data.map((p) => ({ id: p.id, title: p.title })), size) : null;
      },
    }),
    [api, blogId, titles, size],
  );
}
